/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/*
 * The Structural Sound card's list of accepted recordings (Heart Report
 * v0.5.8 at cardiac-panel-v4 git ebcfc04: structuralSourceRecordings,
 * selectStructuralSource, structuralSourceChecklistHTML). The exam roll-up
 * lists every recording accepted for Structural Sound on the card's
 * evidence block (`sourceRecordings`): the one the exam-level result was
 * taken from, the other assessable ones the clinician may choose instead,
 * and the ones that cannot support the result — kept on the list, disabled,
 * with the reason. Choosing a recording makes its scored result the card's
 * value, as a clinician assessment.
 *
 * Deviation from upstream: selecting a recording here only changes the
 * card's value; the notes cascade and the edit-session bookkeeping are the
 * edit action's (edit/edit-actions.js selectStructuralSource), as for every
 * other value change.
 */

import { currentHeartRateBpm } from "./features"
import { calculateMarkerPosition } from "./marker"
import { structuralSoundResultLabel } from "./status"
import { structuralObservedForStatus } from "./exam-rollup"

export const STRUCTURAL_SOURCE_UNAVAILABLE_STATUS = "Unavailable for report selection"
export const STRUCTURAL_SOURCE_UNAVAILABLE_REASON = "No scored structural-sound cycles were available."
export const STRUCTURAL_SOURCE_HINT =
  "Choose one assessable recording as the card evidence. Unavailable recordings remain listed for transparency but cannot be selected."

export function isStructuralSoundReviewFeature(feature) {
  return !!feature && (feature.name === "Structural Sound" || feature.name === "Structural Sound Summary")
}

const finiteNumber = (value) =>
  value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value)) ? Number(value) : null

/** The evidence block's source list as the card keeps it, the result's recording marked selected. */
export function structuralSourceRecordingsFrom(incoming) {
  if (!incoming || !Array.isArray(incoming.sourceRecordings)) return []
  return incoming.sourceRecordings.map((source) => ({
    ...source,
    selected: source.recordingId === incoming.sourceRecordingId || source.selected === true,
  }))
}

export function structuralSourceRecordings(feature) {
  return isStructuralSoundReviewFeature(feature) && Array.isArray(feature.structuralSourceRecordings)
    ? feature.structuralSourceRecordings
    : []
}

export function selectedStructuralSource(feature) {
  return (
    structuralSourceRecordings(feature).find((source) => source.assessable === true && source.selected === true) || null
  )
}

/** What a listed recording says of itself: its classification and peak cycle repeatability, or why it cannot be chosen. */
export function structuralSourceStatusText(source) {
  if (!source || source.assessable !== true) return STRUCTURAL_SOURCE_UNAVAILABLE_STATUS
  const peak = finiteNumber(source.maxCyclePct)
  const classification = source.classification || "Assessed"
  if (peak === null) return classification
  return `${classification} · ${Math.round(peak)}% peak cycles`
}

export function structuralSourceUnavailableReason(source) {
  if (!source || source.assessable === true) return ""
  return source.unavailableReason || STRUCTURAL_SOURCE_UNAVAILABLE_REASON
}

const statusOfClassification = (classification) => {
  const value = String(classification || "").toLowerCase()
  if (value === "red" || value === "elevated") return "red"
  if (value === "amber" || value === "borderline") return "amber"
  return "green"
}

// The list marks the recording and the card takes its cycle percentages
const markStructuralSource = (feature, source) => {
  structuralSourceRecordings(feature).forEach((item) => {
    item.selected = item === source
  })
  feature.sourceRecordingId = source.recordingId || null
  feature.sourcePosition = source.position || null
  feature.sysCyclePct = finiteNumber(source.sysCyclePct)
  feature.diaCyclePct = finiteNumber(source.diaCyclePct)
  feature.maxCyclePct = finiteNumber(source.maxCyclePct) ?? Math.max(feature.sysCyclePct || 0, feature.diaCyclePct || 0)
}

// The card takes the recording's scored result: its cycle percentages, its
// classification as the card's status, and the exam-level wording for it
const takeStructuralSource = (features, feature, source) => {
  markStructuralSource(feature, source)
  const status = statusOfClassification(source.classification)
  feature.status = status
  feature.aiStatus = status === "green" ? "normal" : status === "red" ? "abnormal" : "borderline"
  feature.observed = structuralObservedForStatus(status)
  feature.structuralSoundLabel = structuralSoundResultLabel({ observed: feature.observed }, status)
  feature.markerPosition = calculateMarkerPosition(feature, currentHeartRateBpm(features))
}

/**
 * Makes one listed recording the card's evidence; refused (with the reason)
 * when the recording is not on the list or cannot support the result.
 */
export function applyStructuralSource(features, index, recordingId) {
  const feature = features[index]
  const source = structuralSourceRecordings(feature).find((item) => String(item.recordingId) === String(recordingId))
  if (!source) return { ok: false, reason: "That recording is not among the accepted Structural Sound recordings." }
  if (source.assessable !== true) {
    return { ok: false, reason: structuralSourceUnavailableReason(source) }
  }
  takeStructuralSource(features, feature, source)
  return { ok: true, reason: null }
}

/**
 * Puts a baseline's list and chosen recording back on the card (Cancel and
 * rewind), with the recording's cycle percentages; a baseline from before
 * the list existed leaves the card's list alone.
 */
export function restoreStructuralSources(feature, baseline) {
  if (!isStructuralSoundReviewFeature(feature) || !baseline || !Array.isArray(baseline.structuralSourceRecordings)) {
    return false
  }
  // A field that is already absent stays absent, so that putting a card
  // back leaves its document exactly as it was
  const restore = (key, value) => {
    if ((feature[key] ?? null) !== value) feature[key] = value
  }
  feature.structuralSourceRecordings = baseline.structuralSourceRecordings.map((source) => ({ ...source }))
  restore("sourceRecordingId", baseline.sourceRecordingId || null)
  restore("sourcePosition", baseline.sourcePosition || null)
  syncStructuralSelection([], feature)
  return true
}

/**
 * Re-marks the list for the recording the card's value came from (after a
 * restore or a rebase put that value back), taking the recording's cycle
 * percentages with it and leaving the restored result as it is; a card
 * whose recording is no longer listed keeps the list as the new evidence
 * marked it.
 */
export function syncStructuralSelection(features, feature) {
  const source = structuralSourceRecordings(feature).find(
    (item) =>
      item.assessable === true &&
      feature.sourceRecordingId &&
      String(item.recordingId) === String(feature.sourceRecordingId)
  )
  if (!source) return false
  markStructuralSource(feature, source)
  return true
}
